/**
 * bulkAction/BulkReassignModal.jsx
 *
 * Two-column layout:
 *   LEFT  → pick role + user to reassign FROM → see & select their questions
 *   RIGHT → pick role + user to reassign TO + new due date
 *
 * managementListValue passed from parent (same as Qualitative.jsx pattern).
 *
 * Props:
 *   show, onHide, selectedIds, financialYearId, managementListValue, onSuccess
 */
import { useState, useEffect, useRef } from "react";
import { apiCall } from "../../../../../_services/apiCall";
import config from "../../../../../config/config.json";
import { S, FieldLabel, StyledSelect, Chip, Spin } from "./Shared";

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const BulkReassignModal = ({
  show,
  onHide,
  selectedIds = [],
  financialYearId,
  managementListValue = [],
  onSuccess,
}) => {
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  /* shared user pool — refreshed when a role is picked on either side */
  const [userPool,     setUserPool]     = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  /* FROM side */
  const [fromRoles,  setFromRoles]  = useState([]);
  const [fromUsers,  setFromUsers]  = useState([]);
  const [assignedQs, setAssignedQs] = useState([]);
  const [checkedQIds, setCheckedQIds] = useState([]);
  const [selectAll,   setSelectAll]   = useState(false);
  const [showQPicker, setShowQPicker] = useState(false);

  /* TO side */
  const [selfAssign, setSelfAssign] = useState(false);
  const [toRoles,    setToRoles]    = useState([]);
  const [toUsers,    setToUsers]    = useState([]);
  const [dueDate,    setDueDate]    = useState(todayStr());

  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  /* ── reset on open ── */
  useEffect(() => {
    if (!show) return;
    setUserPool([]);
    setFromRoles([]); setFromUsers([]); setAssignedQs([]); setCheckedQIds([]);
    setSelectAll(false); setShowQPicker(false);
    setToRoles([]); setToUsers([]);
    setSelfAssign(false); setDueDate(todayStr()); setError("");
  }, [show]);

  /* ── select-all effect ── */
  useEffect(() => {
    setCheckedQIds(selectAll ? assignedQs.map((q) => q.questionId) : []);
  }, [selectAll, assignedQs]);

  /* ── fetch users for role ── */
  const fetchUsersForRole = async (roleId) => {
    setLoadingUsers(true);
    setUserPool([]);
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getSubUserBasedOnRoleId`,
        {},
        { roleId: String(roleId) },
        "GET"
      );
      if (isSuccess && isMounted.current) {
        const seen = new Set();
        setUserPool(
          (data?.data || [])
            .map((u) => ({
              id:   String(u.userId),
              name: [u.firstName, u.lastName].filter(Boolean).join(" ") +
                    (u.designation ? ` (${u.designation})` : ""),
            }))
            .filter((u) => {
              if (seen.has(u.id)) return false;
              seen.add(u.id);
              return true;
            })
        );
      }
    } finally {
      if (isMounted.current) setLoadingUsers(false);
    }
  };

  /* ── fetch questions assigned to a user ── */
  const fetchAssignedQs = async (userId) => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getAssignedReportingQuestionDetails`,
        {},
        { userId },
        "GET"
      );
      if (isSuccess && isMounted.current)
        setAssignedQs(data?.data?.data || []);
    } catch {
      setAssignedQs([]);
    }
  };

  /* ── FROM role add ── */
  const addFromRole = (e) => {
    const id = e.target.value;
    if (!id) return;
    e.target.value = "";
    if (fromRoles.some((r) => r.id === id)) return;
    const role = managementListValue.find((r) => String(r.id) === id);
    setFromRoles((p) => [
      ...p,
      { id, name: role?.role_name || role?.name || `Role ${id}` },
    ]);
    fetchUsersForRole(id);
  };

  /* ── FROM user add ── */
  const addFromUser = (e) => {
    const id = e.target.value;
    if (!id) return;
    e.target.value = "";
    if (fromUsers.some((u) => u.id === id)) return;
    const u = userPool.find((x) => x.id === id);
    if (!u) return;
    setFromUsers((p) => [...p, u]);
    fetchAssignedQs(id);
  };

  const removeFromUser = (id) => {
    const next = fromUsers.filter((u) => u.id !== id);
    setFromUsers(next);
    if (!next.length) setAssignedQs([]);
    else fetchAssignedQs(next[next.length - 1].id);
  };

  /* ── TO role add ── */
  const addToRole = (e) => {
    const id = e.target.value;
    if (!id) return;
    e.target.value = "";
    if (toRoles.some((r) => r.id === id)) return;
    const role = managementListValue.find((r) => String(r.id) === id);
    setToRoles((p) => [
      ...p,
      { id, name: role?.role_name || role?.name || `Role ${id}` },
    ]);
    fetchUsersForRole(id);
  };

  /* ── TO user add ── */
  const addToUser = (e) => {
    const id = e.target.value;
    if (!id) return;
    e.target.value = "";
    if (toUsers.some((u) => u.id === id)) return;
    const u = userPool.find((x) => x.id === id);
    if (u) setToUsers((p) => [...p, u]);
  };

  const toggleQ = (qid) =>
    setCheckedQIds((p) =>
      p.includes(qid) ? p.filter((x) => x !== qid) : [...p, qid]
    );

  /* ── save ── */
  const handleSave = async () => {
    setError("");
    if (!fromUsers.length) {
      setError("Please select the user to reassign FROM (left side).");
      return;
    }
    let assignedToIds;
    if (selfAssign) {
      const me = JSON.parse(localStorage.getItem("currentUser") || "{}");
      if (!me?.id) { setError("Could not read current user."); return; }
      assignedToIds = [String(me.id)];
    } else {
      if (!toUsers.length) {
        setError("Please select the user to reassign TO (right side).");
        return;
      }
      assignedToIds = toUsers.map((u) => u.id);
    }

    /* if user picked specific questions in picker, use those; else use all selectedIds */
    const questionIds = checkedQIds.length ? checkedQIds : selectedIds;

    setSaving(true);
    try {
      const { isSuccess } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}reassignedQuestionToUser`,
        {},
        {
          financialYearId,
          questionIds,
          assignedToIds,
          reAssignedToIds: fromUsers.map((u) => u.id),
          dueDate,
          moduleType:        "SQ",
          questionnaireType: "CA",
        },
        "POST"
      );
      if (isSuccess) { onSuccess?.(); onHide(); }
      else setError("Reassignment failed. Please try again.");
    } catch {
      setError("Server error.");
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  if (!show) return null;

  /* ── sub-modal: question picker ── */
  const QuestionPickerModal = () => (
    <div style={{ ...S.overlay, zIndex: 3100 }}>
      <div style={{ ...S.modal, maxWidth: 540 }}>
        <div style={S.header}>
          <span style={S.title}>Select Questions to Reassign</span>
          <button onClick={() => setShowQPicker(false)} style={S.closeBtn}>✕</button>
        </div>

        {/* select all row */}
        <div
          style={{
            padding: "9px 20px",
            borderBottom: "1px solid #f1f5f9",
            display: "flex",
            alignItems: "center",
            gap: 9,
          }}
        >
          <input
            type="checkbox"
            checked={selectAll}
            onChange={(e) => setSelectAll(e.target.checked)}
            id="qa-select-all"
          />
          <label
            htmlFor="qa-select-all"
            style={{ fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer" }}
          >
            Select All ({assignedQs.length})
          </label>
        </div>

        {/* question list */}
        <div style={{ overflowY: "auto", maxHeight: 380, padding: "8px 18px" }}>
          {assignedQs.map((q, i) => {
            const checked = checkedQIds.includes(q.questionId);
            return (
              <div
                key={q.questionId}
                onClick={() => toggleQ(q.questionId)}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 9,
                  padding: "9px 11px",
                  borderRadius: 7,
                  marginBottom: 5,
                  cursor: "pointer",
                  background: checked ? "#eff6ff" : "#f8fafc",
                  border: `1px solid ${checked ? "#93c5fd" : "#e2e8f0"}`,
                  transition: "background 0.1s",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleQ(q.questionId)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, color: "#1e293b", lineHeight: 1.5 }}>
                  <b style={{ color: "#94a3b8", marginRight: 5 }}>{i + 1}.</b>
                  {q.title || `Question ${q.questionId}`}
                </span>
              </div>
            );
          })}
        </div>

        <div style={{ ...S.footer, justifyContent: "space-between" }}>
          <span style={{ fontSize: 13, color: "#64748b" }}>
            {checkedQIds.length} selected
          </span>
          <button onClick={() => setShowQPicker(false)} style={S.btnPrimary}>
            Done
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div style={S.overlay}>
        <div style={{ ...S.modal, maxWidth: 800 }}>
          <div style={S.header}>
            <span style={S.title}>Re-Assign Question</span>
            <button onClick={onHide} disabled={saving} style={S.closeBtn}>✕</button>
          </div>

          <div
            style={{
              ...S.body,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 26,
              alignItems: "start",
            }}
          >
            {error && (
              <div style={{ ...S.errBox, gridColumn: "1 / -1" }}>{error}</div>
            )}

            {/* ═══════ LEFT: FROM ═══════ */}
            <div>
              <div style={S.sectionTitle}>Reassign From</div>

              {/* FROM role */}
              <div style={S.field}>
                <FieldLabel>Role</FieldLabel>
                <StyledSelect
                  value=""
                  onChange={addFromRole}
                  disabled={!managementListValue.length}
                >
                  <option value="">
                    {!managementListValue.length
                      ? "No roles available"
                      : "— Select a Role —"}
                  </option>
                  {managementListValue.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.role_name || r.name}
                    </option>
                  ))}
                </StyledSelect>
                {fromRoles.length > 0 && (
                  <div style={S.chipRow}>
                    {fromRoles.map((r) => (
                      <Chip
                        key={r.id}
                        label={r.name}
                        onRemove={() =>
                          setFromRoles((p) => p.filter((x) => x.id !== r.id))
                        }
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* FROM user */}
              <div style={S.field}>
                <FieldLabel>
                  User {loadingUsers && <Spin />}
                </FieldLabel>
                <StyledSelect
                  value=""
                  onChange={addFromUser}
                  disabled={!fromRoles.length || loadingUsers}
                >
                  <option value="">
                    {!fromRoles.length
                      ? "Select a role first"
                      : loadingUsers
                      ? "Loading…"
                      : "— Select a User —"}
                  </option>
                  {userPool.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </StyledSelect>
                {fromUsers.length > 0 && (
                  <div style={S.chipRow}>
                    {fromUsers.map((u) => (
                      <Chip
                        key={u.id}
                        label={u.name}
                        onRemove={() => removeFromUser(u.id)}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Question count summary */}
              {assignedQs.length > 0 && (
                <div
                  style={{
                    background: "#f8fafc",
                    borderRadius: 7,
                    padding: "9px 13px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}
                    >
                      {assignedQs.length} assigned question
                      {assignedQs.length !== 1 ? "s" : ""}
                      {checkedQIds.length > 0 && (
                        <span style={{ color: "#2563eb" }}>
                          {" "}· {checkedQIds.length} selected
                        </span>
                      )}
                    </span>
                    <button
                      onClick={() => setShowQPicker(true)}
                      style={{
                        fontSize: 12,
                        color: "#2563eb",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: 600,
                        padding: 0,
                      }}
                    >
                      View / Select ↗
                    </button>
                  </div>
                  {!checkedQIds.length && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "#94a3b8",
                        margin: "5px 0 0",
                      }}
                    >
                      All will be reassigned if none selected.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* ═══════ RIGHT: TO ═══════ */}
            <div>
              <div style={S.sectionTitle}>Reassign To</div>

              {!fromUsers.length ? (
                <div style={S.infoBox}>
                  ⬅ Select a user on the left first.
                </div>
              ) : (
                <>
                  {/* self assign */}
                  <label style={S.checkRow}>
                    <input
                      type="checkbox"
                      checked={selfAssign}
                      onChange={(e) => setSelfAssign(e.target.checked)}
                    />
                    <span style={{ fontSize: 13, color: "#334155" }}>
                      Self Assign
                    </span>
                  </label>

                  {!selfAssign && (
                    <>
                      {/* TO role */}
                      <div style={S.field}>
                        <FieldLabel>Role</FieldLabel>
                        <StyledSelect
                          value=""
                          onChange={addToRole}
                          disabled={!managementListValue.length}
                        >
                          <option value="">
                            {!managementListValue.length
                              ? "No roles available"
                              : "— Select a Role —"}
                          </option>
                          {managementListValue.map((r) => (
                            <option key={r.id} value={String(r.id)}>
                              {r.role_name || r.name}
                            </option>
                          ))}
                        </StyledSelect>
                        {toRoles.length > 0 && (
                          <div style={S.chipRow}>
                            {toRoles.map((r) => (
                              <Chip
                                key={r.id}
                                label={r.name}
                                onRemove={() =>
                                  setToRoles((p) =>
                                    p.filter((x) => x.id !== r.id)
                                  )
                                }
                              />
                            ))}
                          </div>
                        )}
                      </div>

                      {/* TO user */}
                      <div style={S.field}>
                        <FieldLabel>
                          User {loadingUsers && <Spin />}
                        </FieldLabel>
                        <StyledSelect
                          value=""
                          onChange={addToUser}
                          disabled={!toRoles.length || loadingUsers}
                        >
                          <option value="">
                            {!toRoles.length
                              ? "Select a role first"
                              : loadingUsers
                              ? "Loading…"
                              : "— Select a User —"}
                          </option>
                          {userPool.map((u) => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </StyledSelect>
                        {toUsers.length > 0 && (
                          <div style={S.chipRow}>
                            {toUsers.map((u) => (
                              <Chip
                                key={u.id}
                                label={u.name}
                                onRemove={() =>
                                  setToUsers((p) =>
                                    p.filter((x) => x.id !== u.id)
                                  )
                                }
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Due date */}
                  <div style={S.field}>
                    <FieldLabel>Due Date</FieldLabel>
                    <input
                      type="date"
                      value={dueDate}
                      min={todayStr()}
                      onChange={(e) => setDueDate(e.target.value)}
                      style={S.dateInput}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div style={S.footer}>
            <button onClick={onHide} disabled={saving} style={S.btnSecondary}>
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ ...S.btnPrimary, opacity: saving ? 0.75 : 1 }}
            >
              {saving ? (
                <>
                  <Spin light size={12} /> Saving…
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>

      {showQPicker && <QuestionPickerModal />}
    </>
  );
};

export default BulkReassignModal;